import { useCallback, useRef } from 'react'
import { useEvent, useIsomorphicLayoutEffect } from 'react-use'
import gsap from 'gsap'
import Image from 'next/image'

import Grid from '@/components/base/Grid'
import ImageParallax from '@/components/base/ImageParallax'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import { useModal } from '@/contexts/modal'

import { singleImageRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type SingleImageProps = {
  image: WPImage
  gallery_ref: string
  aspect?: 'portrait' | 'square'
  align?: 'top' | 'bottom'
}

const SingleImage = ({
  image,
  aspect,
  align,
  gallery_ref
}: SingleImageProps) => {
  const { openModalGallery, needsModalGallery } = useModal()
  const root = useRef<HTMLDivElement>(null!)
  const ratio = useRef<HTMLDivElement>(null!)

  const resize = useCallback(() => {
    const { width } = ratio.current.getBoundingClientRect()
    gsap.set(root.current, { width })
  }, [])

  useIsomorphicLayoutEffect(() => resize(), [resize])

  useEvent('resize', resize)

  return (
    <Motion animation={singleImageRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef}>
          <Grid ref={root}>
            <div
              className={cn([
                'col-span-2 md:col-span-4 md:col-start-2',
                'lg:col-auto lg:row-span-7',
                {
                  'lg:row-start-3': align === 'top',
                  'lg:row-start-5': align === 'bottom'
                }
              ])}
            >
              <div
                ref={ratio}
                className={cn(
                  'relative h-auto w-full overflow-hidden rounded-radius-rounded lg:h-full lg:w-auto',
                  {
                    'aspect-[1/1]': aspect === 'square',
                    'aspect-[4/5]': aspect === 'portrait'
                  }
                )}
                data-motion-wrapper
              >
                <ImageParallax
                  className="h-full w-full rounded-radius-rounded lg:h-full lg:w-auto"
                  data-motion-content
                >
                  {(ref) => (
                    <span className="absolute left-0 top-0 h-full w-full">
                      <Image
                        ref={ref}
                        src={image.url}
                        alt={image.alt}
                        loading="lazy"
                        fill
                        onClick={() => openModalGallery(gallery_ref)}
                        className={cn([
                          'pointer-events-none rounded-radius-rounded object-cover',
                          {
                            'pointer-events-auto cursor-pointer':
                              needsModalGallery(gallery_ref)
                          }
                        ])}
                      />
                    </span>
                  )}
                </ImageParallax>
              </div>
            </div>
          </Grid>
        </Section>
      )}
    </Motion>
  )
}

export default SingleImage
